import React, { useState } from 'react';
import { Box, Button } from '@mui/material';
import { Typography, Card } from '../../../design-system';
import {
  ListAlt,
  LocalHospital,
  FitnessCenter,
  Agriculture,
} from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';
import { useChatContext } from '../../agent';
import { useAuthContext } from '../../../contexts/AuthContext';
import { flowiseService } from '../../agent/services/FlowiseService';

const actions = [
  {
    key: 'register_animal',
    label: 'Quero cadastrar um novo animal.',
    icon: <img src="/vaca.svg" alt="Ícone de vaca" style={{ width: 24, height: 24 }} />,
    message: 'Quero cadastrar um novo animal.',
  },
  {
    key: 'list_animals',
    label: 'Liste todos os animais da minha fazenda.',
    icon: <ListAlt />,
    message: 'Liste todos os animais da minha fazenda.',
  },
  {
    key: 'record_milking',
    label: 'Registre a ordenha de hoje.',
    icon: <Agriculture />,
    message: 'Registre a ordenha de hoje.',
  },
  {
    key: 'record_weight',
    label: 'Registre pesagem para um animal.',
    icon: <FitnessCenter />,
    message: 'Registre pesagem para um animal.',
  },
];

const FarmQuickActionsView: React.FC = () => {
  const { sendMessage } = useChatContext();
  const { authState } = useAuthContext();
  const [selectedFlow, setSelectedFlow] = useState<string>('');

  const onActionClick = async (message: string) => {
    try {
      // Garantir selectedFlow; se não tiver, tenta carregar on-demand
      let flowId = selectedFlow;
      if (!flowId) {
        const userId = authState.profileId;
        if (!userId) {
          console.error('Sem userId para carregar flows');
          return;
        } else {
          try {
            const flows = await flowiseService.getAvailableFlows(userId);
            flowId = flows[0]?.id || '';
            setSelectedFlow(flowId);
          } catch (err) {
            console.error('Erro ao carregar flows on-demand:', err);
            return;
          }
        }
      }

      await sendMessage(message, flowId);
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, message: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActionClick(message);
    }
  };

  return (
    <Box>
      {/* Descrição no topo */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: DESIGN_TOKENS.colors.primary[100],
              color: DESIGN_TOKENS.colors.primary[700],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img src="/logo.svg" alt="Logo da Muu" style={{ width: 32, height: 32 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.primary, mb: 1.5 }}>
              A Muu é uma assistente de inteligência artificial criada para facilitar o dia a dia na fazenda. Ela ajuda você a gerenciar seus animais, acompanhar pesagens, nascimentos, produções, tratamentos e muito mais — tudo de forma simples, rápida e organizada.
            </Typography>
            <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.primary }}>
              Com a Muu, sua fazenda fica na palma da sua mão: dados sempre atualizados, decisões mais assertivas e mais tempo livre para o que realmente importa — o campo.
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Ações Rápidas */}
      <Card sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.text.primary, mb: 2 }}>
          Você pode começar com algumas ações rápidas. Experimente!
        </Typography>

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
          gap: 2,
        }}>
          {actions.map((action) => (
            <Card
              key={action.key}
              sx={{ 
                p: 2, 
                height: '100%', 
                cursor: 'pointer',
                transition: 'transform 0.1s ease, box-shadow 0.2s ease', 
                '&:hover': { 
                  transform: 'translateY(-2px)', 
                  boxShadow: 3 
                }
              }}
              role="button"
              tabIndex={0}
              onClick={() => onActionClick(action.message)}
              onKeyDown={(e) => handleKeyDown(e, action.message)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: DESIGN_TOKENS.colors.primary[50],
                    color: DESIGN_TOKENS.colors.brand.primary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {action.icon}
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {action.label}
                </Typography>
              </Box>
            </Card>
          ))}
        </Box>
      </Card>
    </Box>
  );
};

export default FarmQuickActionsView;